import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { socialMediaService } from '../../services/socialMediaService';
import NavigationBar from '../../components/ui/NavigationBar';
import BreadcrumbTrail from '../../components/ui/BreadcrumbTrail';
import Icon from '../../components/AppIcon';

import SecurityDashboard from './components/SecurityDashboard';
import ConnectionTester from './components/ConnectionTester';
import APIConfigPanel from './components/APIConfigPanel';
import SystemPerformance from './components/SystemPerformance';

const SecurityConnectionManagement = () => {
  const navigate = useNavigate();
  const { user, userProfile, isAdmin } = useAuth();
  const [activeTab, setActiveTab] = useState('security');
  const [connections, setConnections] = useState([]);
  const [loading, setLoading] = useState(true);

  const breadcrumbItems = [
    { label: 'Inicio', path: '/', icon: 'Home' },
    { label: 'Admin Dashboard', path: '/admin-dashboard', icon: 'Shield' },
    { label: 'Seguridad & Conexiones', path: '/security-connection-management', icon: 'Lock' }
  ];

  useEffect(() => {
    if (user?.id && isAdmin) {
      loadConnections();
    }
  }, [user, isAdmin]);

  const loadConnections = async () => {
    try {
      setLoading(true);
      const connectionsData = await socialMediaService?.getConnectionsStatus(user?.id);
      setConnections(connectionsData || []);
    } catch (error) {
      console.error('Error loading connections:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <NavigationBar />
        <div className="flex items-center justify-center h-[calc(100vh-80px)]">
          <div className="text-center">
            <Icon name="Loader2" size={48} className="animate-spin text-primary mx-auto mb-4" />
            <p className="text-muted-foreground">Cargando panel de seguridad...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <NavigationBar />

      <div className="container mx-auto px-4 py-6 sm:py-8 mt-16 sm:mt-20">
        <BreadcrumbTrail items={breadcrumbItems} />

        {/* Header */}
        <div className="mb-6 sm:mb-8">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold text-foreground mb-2 flex items-center">
                <Icon name="Lock" size={32} className="mr-3 text-primary" />
                Seguridad & Gestión de Conexiones
              </h1>
              <p className="text-sm sm:text-base text-muted-foreground">
                Monitoreo de seguridad, pruebas de API y gestión de integraciones
              </p>
            </div>
            <div className="flex items-center space-x-2">
              <div className="flex items-center space-x-2 px-3 py-2 bg-green-100 text-green-800 rounded-lg">
                <Icon name="Shield" size={18} />
                <span className="text-sm font-medium">Sistema Seguro</span>
              </div>
            </div>
          </div>
        </div>

        {/* Tab Navigation */}
        <div className="flex space-x-2 mb-6 border-b border-border overflow-x-auto">
          <button
            onClick={() => setActiveTab('security')}
            className={`px-4 py-2 font-medium transition-colors whitespace-nowrap ${
              activeTab === 'security' ?'text-primary border-b-2 border-primary' :'text-muted-foreground hover:text-foreground'
            }`}
          >
            <Icon name="Shield" size={18} className="inline mr-2" />
            Seguridad
          </button>
          <button
            onClick={() => setActiveTab('connections')}
            className={`px-4 py-2 font-medium transition-colors whitespace-nowrap ${
              activeTab === 'connections' ?'text-primary border-b-2 border-primary' :'text-muted-foreground hover:text-foreground'
            }`}
          >
            <Icon name="Link" size={18} className="inline mr-2" />
            Conexiones
          </button>
          <button
            onClick={() => setActiveTab('api-config')}
            className={`px-4 py-2 font-medium transition-colors whitespace-nowrap ${
              activeTab === 'api-config' ?'text-primary border-b-2 border-primary' :'text-muted-foreground hover:text-foreground'
            }`}
          >
            <Icon name="Settings" size={18} className="inline mr-2" />
            Configuración API
          </button>
          <button
            onClick={() => setActiveTab('performance')}
            className={`px-4 py-2 font-medium transition-colors whitespace-nowrap ${
              activeTab === 'performance'
                ? 'text-primary border-b-2 border-primary' :'text-muted-foreground hover:text-foreground'
            }`}
          >
            <Icon name="Activity" size={18} className="inline mr-2" />
            Rendimiento
          </button>
        </div>

        {/* Tab Content */}
        <div className="space-y-6">
          {activeTab === 'security' && <SecurityDashboard />}
          {activeTab === 'connections' && (
            <ConnectionTester connections={connections} onRefresh={loadConnections} />
          )}
          {activeTab === 'api-config' && <APIConfigPanel />}
          {activeTab === 'performance' && <SystemPerformance />}
        </div>
      </div>
    </div>
  );
};

export default SecurityConnectionManagement;
